// ===============================================================
// ✅ CallsTabContent.js — LIVORA Calls History Tab (FINAL)
// - real filters: all / incoming / outgoing / missed
// - avatars via publicProfiles + profilePhotoResolver
// ===============================================================

import React, { useEffect, useMemo, useRef, useState } from "react";
import { auth, db } from "../../firebase";
import { getFunctions, httpsCallable } from "firebase/functions";
import { collection, doc, getDoc, onSnapshot, query, where } from "firebase/firestore";
import { Phone, RadioTower, PhoneIncoming, PhoneOutgoing, PhoneMissed } from "lucide-react";
import CallHistoryItem from "./CallHistoryItem";
import { defaultAvatarByGender } from "../../utils/defaultAvatar";
import {
  getProfilePhotoRawFromUser,
  resolveProfilePhotoFromUser,
  resolveProfilePhotoUrl,
} from "../../utils/profilePhotoResolver";
import {
  getCallOtherParty,
  getCallDirection,
  getCallVisualState,
  toMillisSafe,
} from "./callHelpers";
import { useCallStartOverlay } from "../../calls/useCallStartOverlay";
import { getStartCallErrorToast } from "../../calls/startCallErrors";
import {
  startCallStartupTraceAt,
  logCallStartupEvent,
} from "../../calls/callStartupTrace";
import {
  buildReturnContextFromWindow,
  saveReturnContext,
} from "../../calls/nativeCallReturnContext";
import useLivoraToast from "../../hooks/useLivoraToast";
import LivoraToast from "../common/LivoraToast";

async function resolveAndCacheOtherPhoto(other, avatarCache) {
  const uid = String(other?.uid || "").trim();
  const gender = String(other?.gender || "");
  const fallback = defaultAvatarByGender(gender);

  if (!uid) {
    console.log("[callHistoryAvatar] fallback used", {
      uid: "",
      source: "no_uid",
    });
    return fallback;
  }

  if (avatarCache.has(uid)) {
    return avatarCache.get(uid);
  }

  try {
    const snap = await getDoc(doc(db, "publicProfiles", uid));
    if (snap.exists()) {
      const profileData = snap.data() || {};
      const raw = getProfilePhotoRawFromUser(profileData);
      if (raw) {
        const url = await resolveProfilePhotoFromUser(profileData, gender);
        avatarCache.set(uid, url);
        console.log("[callHistoryAvatar] profile used", { uid });
        return url;
      }
    }
  } catch (e) {
    console.warn("[callHistoryAvatar] publicProfiles error", {
      uid,
      error: e?.message || e,
    });
  }

  const snapshotRaw = String(other?.photo || "").trim();
  if (snapshotRaw) {
    const url = await resolveProfilePhotoUrl(snapshotRaw, gender);
    avatarCache.set(uid, url);
    console.log("[callHistoryAvatar] fallback used", {
      uid,
      source: "snapshot",
    });
    return url;
  }

  avatarCache.set(uid, fallback);
  console.log("[callHistoryAvatar] fallback used", {
    uid,
    source: "default",
  });
  return fallback;
}

export default function CallsTabContent({ t, navigate }) {
  const [callsLoading, setCallsLoading] = useState(true);
  const [calls, setCalls] = useState([]);
  const [activeFilter, setActiveFilter] = useState("all");

  const myUid = auth.currentUser?.uid || "";
  const avatarCacheRef = useRef(new Map());

  const { begin: beginCallStart, logReturned, logFailed, overlay } =
    useCallStartOverlay();
  const { toast, showToast } = useLivoraToast();

  useEffect(() => {
    const user = auth.currentUser;

    if (!user) {
      setCallsLoading(false);
      return;
    }

    const maleQ = query(
      collection(db, "calls"),
      where("maleUid", "==", user.uid)
    );

    const femaleQ = query(
      collection(db, "calls"),
      where("femaleUid", "==", user.uid)
    );

    let maleDocs = [];
    let femaleDocs = [];

    const rebuild = async () => {
      try {
        const rawList = [...maleDocs, ...femaleDocs];

        const uniqueMap = new Map();
        rawList.forEach((item) => {
          if (item?.id) uniqueMap.set(item.id, item);
        });

        const merged = Array.from(uniqueMap.values());
        const avatarCache = avatarCacheRef.current;

        const otherByCallId = new Map();
        const uniqueOthers = new Map();

        merged.forEach((call) => {
          const other = getCallOtherParty(call, user.uid, t);
          otherByCallId.set(call.id, other);
          if (other?.uid && !uniqueOthers.has(other.uid)) {
            uniqueOthers.set(other.uid, other);
          }
        });

        await Promise.all(
          Array.from(uniqueOthers.values()).map((other) =>
            resolveAndCacheOtherPhoto(other, avatarCache)
          )
        );

        const enriched = merged.map((call) => {
          const other = otherByCallId.get(call.id) || {};
          const gender = other?.gender || "";
          const resolvedPhoto =
            (other?.uid && avatarCache.get(other.uid)) ||
            defaultAvatarByGender(gender);

          return {
            ...call,
            otherUserId: other?.uid || "",
            otherUserGender: gender,
            otherUserName:
              other?.name ||
              other?.displayName ||
              t("chat.user", { defaultValue: "مستخدم" }),
            otherUserPhoto: resolvedPhoto,
            sortAt:
              toMillisSafe(call.createdAt) ||
              toMillisSafe(call.startedAt) ||
              toMillisSafe(call.endedAt) ||
              0,
          };
        });

        enriched.sort((a, b) => Number(b.sortAt || 0) - Number(a.sortAt || 0));

        setCalls(enriched);
        setCallsLoading(false);
      } catch (e) {
        console.error("calls rebuild error:", e);
        setCallsLoading(false);
      }
    };

    const unsubMale = onSnapshot(
      maleQ,
      (snap) => {
        maleDocs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        rebuild();
      },
      (err) => {
        console.error("male calls snapshot error:", err);
        setCallsLoading(false);
      }
    );

    const unsubFemale = onSnapshot(
      femaleQ,
      (snap) => {
        femaleDocs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        rebuild();
      },
      (err) => {
        console.error("female calls snapshot error:", err);
        setCallsLoading(false);
      }
    );

    return () => {
      unsubMale();
      unsubFemale();
    };
  }, [t]);

  const handleRecall = async (call) => {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      navigate("/");
      return;
    }

    const targetUid =
      call?.maleUid === currentUser.uid ? call?.femaleUid : call?.maleUid;

    if (!targetUid) return;

    if (!beginCallStart()) return;

    const callButtonTapAt = performance.now();

    try {
      const startCall = httpsCallable(
        getFunctions(undefined, "us-central1"),
        "startCall"
      );

      const res = await startCall({ femaleUid: targetUid });
      const callId = res.data?.callId;

      if (!callId) {
        logFailed("no_call_id", { femaleUid: targetUid, data: res.data });
        return;
      }

      startCallStartupTraceAt(callId, callButtonTapAt);
      logCallStartupEvent(callId, "call_button_tapped", "caller");
      logCallStartupEvent(callId, "call_created", "caller");

      logReturned(callId, { femaleUid: targetUid, recall: true });
      const returnContext = buildReturnContextFromWindow();
      if (currentUser.uid && returnContext) {
        saveReturnContext(currentUser.uid, callId, returnContext);
        console.log(
          `[callReturn] captured role=caller callId=${callId} url=${returnContext.url}`
        );
      }
      navigate(`/call-outgoing/${callId}`, {
        state: {
          returnTo: returnContext?.url || "/messages",
          returnContext: returnContext || undefined,
        },
      });
    } catch (e) {
      logFailed(e, { femaleUid: targetUid, recall: true });
      console.error("recall error:", e);
      const { type, message } = getStartCallErrorToast(t, e);
      showToast(type, message);
    }
  };

  const callsSummary = useMemo(() => {
    let missed = 0;
    let incoming = 0;
    let outgoing = 0;

    calls.forEach((call) => {
      const direction = getCallDirection(call, myUid);
      const visual = getCallVisualState(call, myUid, t);

      if (direction === "incoming") incoming += 1;
      if (direction === "outgoing") outgoing += 1;
      if (visual.missed) missed += 1;
    });

    return { missed, incoming, outgoing };
  }, [calls, myUid, t]);

  const filteredCalls = useMemo(() => {
    return calls.filter((call) => {
      const direction = getCallDirection(call, myUid);
      const visual = getCallVisualState(call, myUid, t);

      if (activeFilter === "all") return true;
      if (activeFilter === "incoming") return direction === "incoming";
      if (activeFilter === "outgoing") return direction === "outgoing";
      if (activeFilter === "missed") return visual.missed === true;

      return true;
    });
  }, [calls, activeFilter, myUid, t]);

  const filterTabs = [
    {
      key: "all",
      label: t("calls.filter_all", { defaultValue: "All" }),
      count: calls.length,
      icon: <Phone size={15} />,
    },
    {
      key: "incoming",
      label: t("calls.filter_incoming", { defaultValue: "Incoming" }),
      count: callsSummary.incoming,
      icon: <PhoneIncoming size={15} />,
    },
    {
      key: "outgoing",
      label: t("calls.filter_outgoing", { defaultValue: "Outgoing" }),
      count: callsSummary.outgoing,
      icon: <PhoneOutgoing size={15} />,
    },
    {
      key: "missed",
      label: t("calls.filter_missed", { defaultValue: "Missed" }),
      count: callsSummary.missed,
      icon: <PhoneMissed size={15} />,
    },
  ];

  if (callsLoading) {
    return (
      <section className="messages-list">
        <p className="messages-empty">
          {t("calls.loading", { defaultValue: "Loading calls..." })}
        </p>
      </section>
    );
  }

  if (!calls.length) {
    return (
      <div className="la-calls-shell">
        <div className="la-calls-header">
          <div className="la-calls-header-left">
            <div className="la-calls-header-icon">
              <Phone size={18} />
            </div>

            <div>
              <h3 className="la-calls-title">
                {t("calls.history_title", { defaultValue: "Call History" })}
              </h3>
              <p className="la-calls-subtitle">
                {t("calls.header_subtitle", {
                  defaultValue: "Incoming • Outgoing • Missed",
                })}
              </p>
            </div>
          </div>
        </div>

        <div className="la-calls-empty">
          <div className="la-calls-empty-orb" />
          <div className="la-calls-empty-radar">
            <RadioTower size={24} />
          </div>
          <h3>{t("calls.empty", { defaultValue: "No calls yet" })}</h3>
          <p>
            {t("calls.empty_sub", {
              defaultValue: "Start connecting with others and build your history",
            })}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="la-calls-shell">
      <div className="la-calls-header">
        <div className="la-calls-header-left">
          <div className="la-calls-header-icon">
            <Phone size={18} />
          </div>

          <div>
            <h3 className="la-calls-title">
              {t("calls.history_title", { defaultValue: "Call History" })}
            </h3>
            <p className="la-calls-subtitle">
              {t("calls.summary", {
                defaultValue:
                  "Incoming: {{incoming}} • Outgoing: {{outgoing}} • Missed: {{missed}}",
                incoming: callsSummary.incoming,
                outgoing: callsSummary.outgoing,
                missed: callsSummary.missed,
              })}
            </p>
          </div>
        </div>
      </div>

      <div className="la-calls-filters">
        {filterTabs.map((tab) => (
          <button
            key={tab.key}
            type="button"
            className={`la-calls-filter-btn ${
              activeFilter === tab.key ? "active" : ""
            }`}
            onClick={() => setActiveFilter(tab.key)}
          >
            <span className="la-calls-filter-icon">{tab.icon}</span>
            <span className="la-calls-filter-label">{tab.label}</span>
            <span className="la-calls-filter-count">{tab.count}</span>
          </button>
        ))}
      </div>

      <section className="la-calls-list">
        {filteredCalls.length === 0 ? (
          <div className="la-calls-filter-empty">
            <p>
              {t("calls.filter_empty", {
                defaultValue: "No calls in this section",
              })}
            </p>
          </div>
        ) : (
          filteredCalls.map((call) => (
            <CallHistoryItem
              key={call.id}
              call={call}
              myUid={myUid}
              onRecall={handleRecall}
              t={t}
            />
          ))
        )}
      </section>
      {overlay}
      <LivoraToast toast={toast} />
    </div>
  );
}
